import { useState } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { addImageToProperty, setPropertyThumbnail } from '../../../firebase/property.service';

/**
 * Hook for uploading property images to Firebase Storage
 * and syncing URLs to Firestore
 *
 * @param {string} userId - Owner's user ID (auth.uid)
 * @param {string} propertyId - Property document ID
 * @param {Function} onUrlUpdate - Callback to update parent state immediately after upload
 *                                  Signature: (imageUrl) => void
 */
export function usePropertyImageUpload({ userId, propertyId, onUrlUpdate }) {
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);

    const storage = getStorage();

    /**
     * Helper to generate unique filename with timestamp
     */
    const generateFilename = (originalFilename) => {
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 8);
        const ext = originalFilename.split('.').pop() || 'jpg';
        return `${timestamp}_${random}.${ext}`;
    };

    /**
     * Extract storage path from Firebase download URL
     * Format: https://firebasestorage.googleapis.com/v0/b/bucket/o/path%2Fto%2Ffile?...
     */
    const extractStoragePath = (downloadUrl) => {
        try {
            const encoded = downloadUrl.split('/o/')[1].split('?')[0];
            return decodeURIComponent(encoded);
        } catch (e) {
            return null;
        }
    };

    /**
     * Upload single image
     * @param {File} file - Image file to upload
     * @returns {Promise<string|null>} - Download URL on success, null on failure
     */
    const uploadImage = async (file) => {
        console.log('[PropertyImageUpload] Starting upload', {
            file: file?.name,
            userId,
            propertyId,
        });

        if (!file || !userId || !propertyId) {
            const msg = `Missing required data: file=${!!file}, userId=${!!userId}, propertyId=${!!propertyId}`;
            console.error('[PropertyImageUpload] Error:', msg);
            setUploadError(msg);
            return null;
        }

        if (!file.type.startsWith('image/')) {
            const msg = `Invalid file type: ${file.type}. Expected image.`;
            console.warn('[PropertyImageUpload]', msg);
            setUploadError(msg);
            return null;
        }

        setUploading(true);
        setUploadError(null);

        try {
            const filename = generateFilename(file.name);
            const storagePath = `propertyImages/${userId}/${propertyId}/${filename}`;
            const storageRef = ref(storage, storagePath);

            console.log('[PropertyImageUpload] Uploading to Storage', { storagePath });
            await uploadBytes(storageRef, file);

            console.log('[PropertyImageUpload] Upload complete, fetching download URL');
            const downloadUrl = await getDownloadURL(storageRef);
            console.log('[PropertyImageUpload] Download URL obtained:', downloadUrl.substring(0, 60) + '...');

            // Add URL to Firestore property document
            console.log('[PropertyImageUpload] Adding URL to property imageUrls array');
            await addImageToProperty(propertyId, downloadUrl);

            // Set as thumbnail if this is the first image
            const storagePath2 = extractStoragePath(downloadUrl);
            if (storagePath2) {
                console.log('[PropertyImageUpload] Checking if thumbnail should be set');
                // Let parent component handle thumbnail logic
                // (they know if imageUrls was empty before)
            }

            console.log('[PropertyImageUpload] Upload successful');

            // Notify parent to update local state immediately
            if (onUrlUpdate) {
                onUrlUpdate(downloadUrl);
            }

            return downloadUrl;
        } catch (err) {
            const errMsg = `${err.code || 'UNKNOWN_ERROR'}: ${err.message}`;
            console.error('[PropertyImageUpload] Error:', errMsg, err);
            setUploadError(errMsg);
            return null;
        } finally {
            setUploading(false);
        }
    };

    /**
     * Upload multiple images in sequence
     * @param {FileList|File[]} files - Images to upload
     * @returns {Promise<string[]>} - Array of download URLs
     */
    const uploadImages = async (files) => {
        const fileArray = Array.from(files);
        const urls = [];

        for (const file of fileArray) {
            const url = await uploadImage(file);
            if (url) {
                urls.push(url);
            }
        }

        return urls;
    };

    /**
     * Set an image as the property thumbnail
     * @param {string} imageUrl - Full download URL to set as thumbnail
     * @returns {Promise<void>}
     */
    const setAsThumbnail = async (imageUrl) => {
        if (!propertyId || !imageUrl) {
            setUploadError('Missing propertyId or imageUrl');
            return;
        }

        try {
            console.log('[PropertyImageUpload] Setting thumbnail', { propertyId });
            setUploadError(null);
            await setPropertyThumbnail(propertyId, imageUrl);
            console.log('[PropertyImageUpload] Thumbnail set successfully');
        } catch (err) {
            console.error('[PropertyImageUpload] Error setting thumbnail:', err);
            setUploadError(err.message);
        }
    };

    return {
        uploading,
        uploadError,
        uploadImage,
        uploadImages,
        setAsThumbnail,
    };
}
